import { useState, useRef } from 'react';
import {
  Container,
  Grid,
  Chip,
  FormControl,
  Input,
  Typography,
  Stack,
  Card,
  CardHeader,
  Box,
  TextField,
} from '@mui/material';
import { useSelector } from 'react-redux';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import IconButton from '@mui/material/IconButton';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { LoadingButton } from '@mui/lab';
import PhotoSensor from '@mui/icons-material/PhotoCamera';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import LinearProgress from '@mui/material/LinearProgress';
import axiosInstance from '../../utils/axiosInstance';
import MultiSelector from '../multi-selector/multiSelector';

const multiInputStyle = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  border: '1px solid lightgray',
  borderRadius: '4px',
  padding: 2,
  gap: '6px',
};

const chipStyle = {
  gap: '6px',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  padding: '1px 3px',
};

export default function PictureApiTest() {
  const sensorList = useSelector((state) => state.app.sensorList);
  const [dateTimeValues, setDateTimeValues] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [successCount, setSuccessCount] = useState(0);
  const [totalReq, setTotalReq] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [currValue, setCurrValue] = useState('');
  const [selectedSensorList, setSelectedSensorList] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [testState, setTestState] = useState('stopped');
  const [iteration, setIteration] = useState(0);
  const timerRef = useRef();
  const widthRef = useRef();
  const heightRef = useRef();
  const intervalRef = useRef();

  const handleKeyUp = (e) => {
    if (e.keyCode === 13) {
      setDateTimeValues((oldState) => [...oldState, e.target.value]);
      setCurrValue('');
    }
  };

  const handleChange = (e) => {
    setCurrValue(e.target.value);
  };

  const handleSelectedSensorListChange = (val) => {
    setSelectedSensorList(val);
  };

  const handleInputValue = (val) => {
    setInputValue(val);
  };

  const handleDelete = (item, index) => {
    const arr = [...dateTimeValues];
    arr.splice(index, 1);
    setDateTimeValues(arr);
  };

  const handleAddCurrentTime = () => {
    const event = new Date();
    setDateTimeValues((prevArray) => [...prevArray, event.toISOString()]);
  };

  const parseUnixOrUtcTime = (val) => {
    if (val.includes('Z')) {
      console.log('Already in UTC', val);
      return val;
    }
    const timeInUTCFormat = new Date(Number(val)).toISOString();
    console.log('converted to UTC', timeInUTCFormat);
    return timeInUTCFormat;
  };

  const handlePictureApiTest = () => {
    setItemData([]);
    setSuccessCount(0);
    setFailCount(0);

    if (dateTimeValues.length > 0) {
      setTotalReq(selectedSensorList.length * dateTimeValues.length);
      for (let i = 0; i < selectedSensorList.length; i += 1) {
        for (let j = 0; j < dateTimeValues.length; j += 1) {
          console.group(selectedSensorList[i].name);
          console.log(selectedSensorList[i].host, selectedSensorList[i].sensorId);
          console.log(dateTimeValues[j]);
          console.groupEnd();
          const extractedTime = parseUnixOrUtcTime(dateTimeValues[j]);
          let api = '';
          if (
            widthRef.current.value &&
            widthRef.current.value !== '' &&
            heightRef.current.value &&
            heightRef.current.value !== ''
          ) {
            api = `${window.location.protocol}//${selectedSensorList[i].host}/api/v1/replay/stream/${selectedSensorList[i].sensorId}/picture?startTime=${extractedTime}&width=${widthRef.current.value}&height=${heightRef.current.value}`;
          } else {
            api = `${window.location.protocol}//${selectedSensorList[i].host}/api/v1/replay/stream/${selectedSensorList[i].sensorId}/picture?startTime=${extractedTime}`;
          }

          axiosInstance
            .get(api, { responseType: 'blob' })
            .then((response) => {
              const binaryData = [];
              binaryData.push(response.data);
              const jsonData = {
                source: window.URL.createObjectURL(new Blob(binaryData, { type: 'image/jpeg' })),
                title: selectedSensorList[i].name,
                dateTime: dateTimeValues[j],
              };
              setItemData((prevArray) => [...prevArray, jsonData]);
              setSuccessCount((prevCount) => prevCount + 1);
            })
            .catch((error) => {
              const jsonData = {
                source: null,
                title: `${selectedSensorList[i].name}, Error: ${error.response.status}`,
                dateTime: dateTimeValues[j],
              };
              setItemData((prevArray) => [...prevArray, jsonData]);
              setFailCount((prevCount) => prevCount + 1);
              console.error('Picture api error', error);
            });
        }
      }
    } else {
      setTotalReq(selectedSensorList.length);
      for (let i = 0; i < selectedSensorList.length; i += 1) {
        console.group(selectedSensorList[i].name);
        console.log(selectedSensorList[i].host, selectedSensorList[i].sensorId);
        console.groupEnd();
        axiosInstance
          .get(
            `${window.location.protocol}//${selectedSensorList[i].host}/api/v1/live/stream/${selectedSensorList[i].sensorId}/picture`,
            { responseType: 'blob' }
          )
          .then((response) => {
            const binaryData = [];
            binaryData.push(response.data);
            const jsonData = {
              source: window.URL.createObjectURL(new Blob(binaryData, { type: 'image/jpeg' })),
              title: selectedSensorList[i].name,
              dateTime: 'Time not provided',
            };
            setItemData((prevArray) => [...prevArray, jsonData]);
            setSuccessCount((prevCount) => prevCount + 1);
          })
          .catch((error) => {
            const jsonData = {
              source: null,
              title: `${selectedSensorList[i].name}, Error: ${error.response.status}`,
              dateTime: 'Time not provided',
            };
            setItemData((prevArray) => [...prevArray, jsonData]);
            setFailCount((prevCount) => prevCount + 1);
            console.error('Picture api error', error);
          });
      }
    }
  };

  const handleAutoTest = () => {
    if (testState === 'stopped') {
      setIteration(0);
      setIteration((prev) => prev + 1);
      handlePictureApiTest();
      timerRef.current = setInterval(() => {
        handlePictureApiTest();
        setIteration((prev) => prev + 1);
      }, intervalRef.current.value);
      setTestState('running');
    } else {
      clearInterval(timerRef.current);
      setTestState('stopped');
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" sx={{ mb: 5 }}>
        Picture API
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title={'Select sensors'} subheader={'Select one or more sensor'} />
            <Box sx={{ p: 3 }}>
              <Stack spacing={3} sx={{ mb: 2 }}>
                <MultiSelector
                  id="picture-api-sensor-selector"
                  placeholder="Type to search"
                  label={`Select sensors`}
                  value={selectedSensorList}
                  inputValue={inputValue}
                  onInputChange={handleInputValue}
                  onChange={handleSelectedSensorListChange}
                  getOptionLabel={(option) => {
                    if (option.remoteDeviceName && option.remoteDeviceLocation) {
                      return `${option.name} at ${option.remoteDeviceName} (${option.remoteDeviceLocation})`;
                    } if (option.remoteDeviceName) {
                      return `${option.name} at ${option.remoteDeviceName}`;
                    }
                    return option.name;

                  }}
                  options={sensorList}
                />
              </Stack>
              <Typography variant="body2">
                Enter time in unix timestamp in milliseconds OR in standard UTC format. Press <b>Enter key</b> to
                provide multiple values
              </Typography>
              <FormControl sx={multiInputStyle}>
                <div style={{ chipStyle }}>
                  {dateTimeValues.map((item, index) => (
                    <Chip
                      sx={{ m: 0.5 }}
                      key={index}
                      size="small"
                      onDelete={() => handleDelete(item, index)}
                      label={item}
                    />
                  ))}
                </div>
                <Input value={currValue} onChange={handleChange} onKeyDown={handleKeyUp} />
              </FormControl>
              <br />
              <TextField id="pic-width" name="width" label="Width" inputRef={widthRef} sx={{ mr: 1 }} />
              <TextField id="pic-height" name="height" label="Height" inputRef={heightRef} sx={{ mr: 1 }} />
              <TextField
                id="pic-interval"
                name="interval"
                label="Interval(ms)"
                inputRef={intervalRef}
                defaultValue={10000}
              />
              <br />
              <br />
              <Typography variant="h6">{`Iteration: ${iteration}`}</Typography>
              <Box sx={{ mt: 2 }} display="flex" justifyContent="flex-end" alignItems="flex-end">
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handleAddCurrentTime}
                  endIcon={<MoreTimeIcon />}
                  sx={{ mr: 2 }}
                >
                  Put current time
                </LoadingButton>
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handlePictureApiTest}
                  endIcon={<PhotoSensor />}
                  sx={{ mr: 2 }}
                >
                  Get Pictures
                </LoadingButton>
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handleAutoTest}
                  sx={{ background: testState === 'running' ? 'red' : '' }}
                >
                  {testState === 'running' ? 'Stop test' : 'Auto test'}
                </LoadingButton>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <Box sx={{ p: 3 }}>
              {totalReq > 0 && (
                <Box sx={{ width: '100%', pb: 1 }}>
                  <LinearProgress variant="determinate" value={((successCount + failCount) / totalReq) * 100} />
                </Box>
              )}
              <ImageList>
                {itemData.map((item, index) => (
                  <ImageListItem key={index} sx={{ border: '2px dotted black' }}>
                    <img style={{ p: 1 }} src={item.source} alt={item.title} loading="lazy" />
                    <ImageListItemBar
                      title={item.title}
                      subtitle={item.dateTime}
                      actionIcon={
                        <IconButton
                          onClick={() => {
                            window.open(item.source, '_blank');
                          }}
                          disabled={item.source == null}
                        >
                          <OpenInFullIcon />
                        </IconButton>
                      }
                      position="below"
                      sx={{ pl: 1 }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
